import React, { useEffect, useState } from 'react';

import { XMarkIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { PRODUCT_COMPLAINT_TYPES } from '@/const/products';
import '@/css/productComplaintModalStyle.css';
import { Dialog, Fade } from '@mui/material';

const MAX_COMPLAINT_TEXT_LENGTH = 200;

export interface ProductComplaintPayload {
  complaintType: string;
  productComplaintText: string;
}

interface ProductComplaintPopupProps {
  isOpen: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (payload: ProductComplaintPayload) => void;
}

const ProductComplaintPopup = ({ isOpen, isSubmitting = false, onClose, onConfirm }: ProductComplaintPopupProps) => {
  const [complaintType, setComplaintType] = useState('');
  const [complaintText, setComplaintText] = useState('');
  const [typeError, setTypeError] = useState(false);
  const [textError, setTextError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setComplaintType('');
    setComplaintText('');
    setTypeError(false);
    setTextError(false);
  }, [isOpen]);

  const handleClickType = (type: string) => {
    setComplaintType(type);
    setTypeError(false);
  };

  const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;

    setComplaintText(value.slice(0, MAX_COMPLAINT_TEXT_LENGTH));
    if (value.trim()) {
      setTextError(false);
    }
  };

  const handleConfirm = () => {
    if (isSubmitting) {
      return;
    }

    const isTypeEmpty = !complaintType;
    const isTextEmpty = !complaintText.trim();

    setTypeError(isTypeEmpty);
    setTextError(isTextEmpty);

    if (isTypeEmpty || isTextEmpty) {
      return;
    }

    onConfirm({ complaintType, productComplaintText: complaintText.trim() });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="product-complaint-dialog-title"
      aria-describedby="product-complaint-dialog-description"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 100 }}
      PaperProps={{ className: 'product-complaint-modal' }}
    >
      <div className="px-5 pt-5 pb-6 sm280:px-4 sm280:pt-4 sm280:pb-5">
        <div className="flex justify-end">
          <button type="button" onClick={onClose} aria-label="닫기" className="p-1 -mr-1">
            <XMarkIcon color={colors.gray[9]} />
          </button>
        </div>

        <h2
          id="product-complaint-dialog-title"
          className="mt-1 font-bold text-gray-9 text-[22px] leading-[30px] sm280:text-[19px] sm280:leading-[26px]"
        >
          매물 신고
        </h2>
        <p
          id="product-complaint-dialog-description"
          className="mt-2 break-keep text-gray-7 text-[15px] leading-[22px] sm280:text-[13px] sm280:leading-[20px]"
        >
          고객님께서 <span className="text-red">허위매물로 인해 겪은 불편사항</span>을 적어주세요. 신고 내용은
          비공개입니다.
        </p>

        <h3 className="mt-6 font-semibold text-gray-9 text-[16px] leading-[22px] sm280:text-[15px]">신고 항목</h3>
        <div className="grid grid-cols-3 gap-[6px] mt-3">
          {PRODUCT_COMPLAINT_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleClickType(type)}
              aria-pressed={complaintType === type}
              className={`h-[44px] px-0.5 break-keep tracking-tight rounded-[8px] border text-[12px] leading-[16px] sm280:text-[11px] sm280:h-[40px] ${
                complaintType === type
                  ? 'border-primary-10 bg-primary-1 text-primary-10 font-semibold'
                  : 'border-gray-3 text-gray-7'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
        {typeError && <p className="mt-2 text-red text-[13px] leading-[18px]">신고 항목을 선택해주세요.</p>}

        <h3 className="mt-6 font-semibold text-gray-9 text-[16px] leading-[22px] sm280:text-[15px]">신고 내용</h3>
        <div
          className={`mt-3 rounded-[8px] border px-3 py-3 focus-within:border-primary-10 ${
            textError ? 'border-red' : 'border-gray-3'
          }`}
        >
          <textarea
            name="productComplaintText"
            className="w-full min-h-[104px] bg-transparent resize-none text-[15px] leading-[22px] text-gray-8 placeholder:text-gray-5 focus:outline-none sm280:min-h-[88px] sm280:text-[14px]"
            placeholder="신고내용을 적어주세요."
            maxLength={MAX_COMPLAINT_TEXT_LENGTH}
            value={complaintText}
            onChange={handleChangeText}
          />
          <div className="text-right text-gray-6 text-[13px] leading-[18px]">
            {complaintText.length}/{MAX_COMPLAINT_TEXT_LENGTH}
          </div>
        </div>
        {textError && <p className="mt-2 text-red text-[13px] leading-[18px]">신고 내용을 적어주세요.</p>}
      </div>

      <div className="flex items-center w-full text-center border-t">
        <div
          className="w-full cursor-pointer text-gray-6 font-semibold py-[17px] text-[20px] leading-[20px] sm280:text-[16px] sm280:leading-[16px]"
          onClick={onClose}
        >
          취소
        </div>
        <div
          className={`w-full border-l cursor-pointer text-primary-10 font-semibold py-[17px] text-[20px] leading-[20px] sm280:text-[16px] sm280:leading-[16px] ${
            isSubmitting ? 'opacity-50' : ''
          }`}
          onClick={handleConfirm}
        >
          확인
        </div>
      </div>
    </Dialog>
  );
};

export default ProductComplaintPopup;
